import { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../../contexts/AuthContext';
import { ROLES } from '../../../constants/roles';
import { masterDataTypes } from '../constants/masterDataConstants';
import { getCurrentDataType, formatCellValue } from '../utils/masterDataUtils';

export const useMasterDataManagement = (dataTypeIndex = 0) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [manufacturerSubTab, setManufacturerSubTab] = useState(0);
  const [data, setData] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [assemblyTypes, setAssemblyTypes] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [models, setModels] = useState([]);
  const [ataChapters, setAtaChapters] = useState([]);
  const [manufacturerNames, setManufacturerNames] = useState([]);
  const [manufacturerCategories, setManufacturerCategories] = useState([]);
  const [assemblyTypeConfigurations, setAssemblyTypeConfigurations] = useState([]);
  const [partCategories, setPartCategories] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteDependencies, setDeleteDependencies] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [configDetailsDialogOpen, setConfigDetailsDialogOpen] = useState(false);
  const [selectedConfiguration, setSelectedConfiguration] = useState(null);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchQuery, setSearchQuery] = useState('');

  const currentType = masterDataTypes[dataTypeIndex];
  const effectiveType = getCurrentDataType(currentType, manufacturerSubTab);

  // Handle navigation state for Manufacturer sub-tab
  useEffect(() => {
    if (location.state?.openSubTab !== undefined && currentType?.key === 'manufacturers') {
      setManufacturerSubTab(location.state.openSubTab);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.openSubTab, currentType?.key, navigate, location.pathname]);

  const fetchData = async (signal = null) => {
    setLoading(true);
    const requestConfig = signal ? { signal } : {};
    try {
      const params = showArchived ? {} : { isActive: 'true' };
      // For Primary Models subtab, include Primary Models in the response
      if (currentType.key === 'manufacturers' && manufacturerSubTab === 2) {
        params.includePrimaryModels = 'true';
      }
      const response = await axios.get(effectiveType.endpoint, { params, ...requestConfig });
      let fetchedData = response.data;

      if (currentType.key === 'manufacturers' && manufacturerSubTab === 2) {
        fetchedData = fetchedData.filter(model => model.type && (!model.forAssembly || model.forAssembly === ''));
      }

      setData(fetchedData);

      if (currentType.key === 'manufacturers' && manufacturerSubTab === 3) {
        // Model Variants subtab - fetch all models for dropdown
        const allModelsResponse = await axios.get('/api/master-data/models', {
          params: { includePrimaryModels: 'true' },
          ...requestConfig
        });
        setModels(allModelsResponse.data || []);
      }
    } catch (error) {
      if ((axios.isCancel && axios.isCancel(error)) || error?.code === 'ERR_CANCELED') return;
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAssemblyTypes = async () => {
    try {
      const response = await axios.get('/api/master-data/assembly-types', {
        params: { isActive: 'true' }
      });
      setAssemblyTypes(response.data || []);
    } catch (error) {
      console.error('Error fetching assembly types:', error);
    }
  };

  const fetchAssemblyTypeConfigurations = async () => {
    try {
      const response = await axios.get('/api/master-data/assembly-type-configurations', {
        params: { isActive: 'true' }
      });
      setAssemblyTypeConfigurations(response.data || []);
    } catch (error) {
      console.error('Error fetching assembly type configurations:', error);
    }
  };

  const fetchManufacturers = async () => {
    try {
      const response = await axios.get('/api/master-data/manufacturers', {
        params: { isActive: 'true' }
      });
      setManufacturers(response.data || []);
    } catch (error) {
      console.error('Error fetching manufacturers:', error);
    }
  };

  const fetchModels = async (includePrimaryModels = false) => {
    try {
      const params = {};
      if (includePrimaryModels) {
        params.includePrimaryModels = 'true';
      }
      const response = await axios.get('/api/master-data/models', { params });
      setModels(response.data || []);
    } catch (error) {
      console.error('Error fetching models:', error);
    }
  };

  const fetchAtaChapters = async () => {
    try {
      const response = await axios.get('/api/master-data/ata-chapters', {
        params: { isActive: 'true' }
      });
      setAtaChapters(response.data || []);
    } catch (error) {
      console.error('Error fetching ATA chapters:', error);
    }
  };

  const fetchManufacturerNames = async () => {
    try {
      const response = await axios.get('/api/master-data/manufacturer-names', {
        params: { isActive: 'true' }
      });
      setManufacturerNames(response.data || []);
    } catch (error) {
      console.error('Error fetching manufacturer names:', error);
    }
  };

  const fetchManufacturerCategories = async () => {
    try {
      const response = await axios.get('/api/master-data/manufacturer-categories', {
        params: { isActive: 'true' }
      });
      setManufacturerCategories(response.data || []);
    } catch (error) {
      console.error('Error fetching manufacturer categories:', error);
    }
  };

  const fetchPartCategories = async () => {
    try {
      const response = await axios.get('/api/master-data/part-categories', {
        params: { isActive: 'true' }
      });
      setPartCategories(response.data || []);
    } catch (error) {
      console.error('Error fetching part categories:', error);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    // Clear data when switching tabs/subtabs to prevent rendering stale data
    setData([]);
    // Reset sorting and search when switching tabs
    setSortField(null);
    setSortDirection('asc');
    setSearchQuery('');
    fetchData(signal);
    if (currentType.key === 'assembly-type-configurations') {
      fetchAssemblyTypes();
      fetchAssemblyTypeConfigurations();
      fetchAtaChapters();
    }
    if (currentType.key === 'manufacturers') {
      fetchManufacturerNames();
      fetchManufacturerCategories();
      fetchManufacturers();
      if (manufacturerSubTab === 2 || manufacturerSubTab === 3) {
        fetchModels(true);
      }
      if (manufacturerSubTab === 3) {
        // Model Variants subtab - fetch manufacturer names for dropdown
        fetchManufacturerNames();
      }
    }
    if (currentType.key === 'assembly-types') {
      fetchAtaChapters();
    }
    if (currentType.key === 'part-categories') {
      fetchPartCategories();
    }
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataTypeIndex, showArchived, manufacturerSubTab]);

  const handleAdd = () => {
    setSelectedItem(null);
    const initialData = currentType.key === 'assembly-type-configurations' ? { columns: [], selectedAtaChapterId: '' } : {};
    if (currentType.key === 'agencies') {
      initialData.name = '';
      initialData.code = '';
      initialData.types = [];
      initialData.contactInfo = { email: '', phone: '', address: '' };
      initialData.certifications = [];
    }
    if (currentType.key === 'operators') {
      initialData.name = '';
      initialData.code = '';
      initialData.address = '';
      initialData.gmail = '';
      initialData.number = '';
      initialData.personToContact = [{ name: '', number: '' }];
      initialData.template = '';
      initialData.templateFileName = '';
      initialData.workOrderFormTemplate = 'CAME_FORM_04';
    }
    if (currentType.key === 'manufacturers') {
      if (manufacturerSubTab === 0) {
        initialData.name = '';
      } else if (manufacturerSubTab === 1) {
        initialData.category = '';
      } else if (manufacturerSubTab === 2) {
        initialData.name = '';
        initialData.manufacturer = '';
        initialData.category = '';
        initialData.type = '';
      } else if (manufacturerSubTab === 3) {
        // Model Variants
        initialData.name = '';
      }
    }
    
    setFormData(initialData);
    setOpen(true);
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    const editData = { ...item };
    
    if (item.assemblyType && typeof item.assemblyType === 'object' && item.assemblyType._id) {
      editData.assemblyType = item.assemblyType._id;
      // Derive selectedAtaChapterId from assemblyType for editing
      // Look up the assembly type from assemblyTypes array to get its ataChapterId
      const assemblyTypeId = item.assemblyType._id;
      const foundAssemblyType = assemblyTypes.find(at => String(at._id) === String(assemblyTypeId));
      if (foundAssemblyType?.ataChapterId) {
        const ataChapterId = typeof foundAssemblyType.ataChapterId === 'object' && foundAssemblyType.ataChapterId?._id
          ? foundAssemblyType.ataChapterId._id
          : foundAssemblyType.ataChapterId;
        editData.selectedAtaChapterId = ataChapterId;
      }
    }
    
    if (currentType.key === 'manufacturers' && (manufacturerSubTab === 2 || manufacturerSubTab === 3) && 
        item.manufacturer && typeof item.manufacturer === 'object' && item.manufacturer._id) {
      editData.manufacturer = item.manufacturer._id;
    }
    
    if (currentType.key === 'manufacturers' && manufacturerSubTab === 2 && 
        item.category && typeof item.category === 'object' && item.category._id) {
      editData.category = item.category._id;
    }
    
    if (currentType.key === 'manufacturers' && manufacturerSubTab === 3 && item.primaryModel && typeof item.primaryModel === 'object' && item.primaryModel._id) {
      editData.primaryModel = item.primaryModel._id;
    }
    
    if (item.ataChapterId && typeof item.ataChapterId === 'object' && item.ataChapterId._id) {
      editData.ataChapterId = item.ataChapterId._id;
    }
    
    if (currentType.key === 'part-categories' && item.primaryCategory && typeof item.primaryCategory === 'object' && item.primaryCategory._id) {
      editData.primaryCategory = item.primaryCategory._id;
    }
    if (currentType.key === 'agencies') {
      editData.types = Array.isArray(item.types) ? [...item.types] : [];
      editData.contactInfo = item.contactInfo && typeof item.contactInfo === 'object' && !Array.isArray(item.contactInfo)
        ? { email: item.contactInfo.email || '', phone: item.contactInfo.phone || '', address: item.contactInfo.address || '' }
        : { email: '', phone: '', address: '' };
      editData.certifications = Array.isArray(item.certifications) ? [...item.certifications] : [];
    }
    if (currentType.key === 'operators') {
      editData.personToContact = Array.isArray(item.personToContact) && item.personToContact.length
        ? item.personToContact.map((person) => ({ name: person.name || '', number: person.number || '' }))
        : [{ name: '', number: '' }];
    }
    if (item.columns && Array.isArray(item.columns)) {
      editData.columns = item.columns;
    } else if (item.columns && typeof item.columns === 'string') {
      try {
        editData.columns = JSON.parse(item.columns);
      } catch {
        editData.columns = [];
      }
    } else if (!item.columns) {
      editData.columns = [];
    }
    
    setFormData(editData);
    setOpen(true);
  };

  const handleRestore = async (item) => {
    const itemType = (currentType.key === 'manufacturers' && manufacturerSubTab === 3)
      ? 'model variant' 
      : currentType.key === 'assembly-types' 
      ? 'assembly type' 
      : currentType.key === 'assembly-type-configurations'
      ? 'assembly type configuration'
      : effectiveType.label.slice(0, -1).toLowerCase();
    
    if (!window.confirm(`Are you sure you want to restore this ${itemType}?`)) return;
    
    try {
      await axios.put(`${effectiveType.endpoint}/${item._id}`, { isActive: true });
      alert(`${itemType.charAt(0).toUpperCase() + itemType.slice(1)} restored successfully`);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.message || `Error restoring ${itemType}`);
    }
  };

  const handleDelete = async (item) => {
    if (currentType.key === 'manufacturers' && manufacturerSubTab === 3) {
      // Model Variants
      if (!item.isActive) {
        setItemToDelete(item);
        setDeleteLoading(true);
        try {
          const dependenciesResponse = await axios.get(`${effectiveType.endpoint}/${item._id}/dependencies`);
          setDeleteDependencies(dependenciesResponse.data);
          setDeleteDialogOpen(true);
        } catch (error) {
          console.error('Error checking dependencies:', error);
          alert('Error checking dependencies. Please try again.');
        } finally {
          setDeleteLoading(false);
        }
      } else {
        const itemType = 'model variant';
        if (!window.confirm(`Are you sure you want to archive this ${itemType}?`)) return;
        try {
          await axios.delete(`${effectiveType.endpoint}/${item._id}`);
          fetchData();
        } catch (error) {
          alert(error.response?.data?.message || `Error archiving ${itemType}`);
        }
      }
    } else if (currentType.key === 'manufacturers' && manufacturerSubTab === 0) {
      // Manufacturer Names
      if (!item.isActive) {
        setItemToDelete(item);
        setDeleteLoading(true);
        try {
          const dependenciesResponse = await axios.get(`${effectiveType.endpoint}/${item._id}/dependencies`);
          setDeleteDependencies(dependenciesResponse.data);
          setDeleteDialogOpen(true);
        } catch (error) {
          console.error('Error checking dependencies:', error);
          alert('Error checking dependencies. Please try again.');
        } finally {
          setDeleteLoading(false);
        }
      } else {
        const itemType = 'manufacturer name';
        if (!window.confirm(`Are you sure you want to archive this ${itemType}?`)) return;
        try {
          await axios.delete(`${effectiveType.endpoint}/${item._id}`);
          fetchData();
        } catch (error) {
          alert(error.response?.data?.message || `Error archiving ${itemType}`);
        }
      }
    } else if (currentType.key === 'manufacturers' && manufacturerSubTab === 1) {
      // Aircraft Categories
      if (!item.isActive) {
        setItemToDelete(item);
        setDeleteLoading(true);
        try {
          const dependenciesResponse = await axios.get(`${effectiveType.endpoint}/${item._id}/dependencies`);
          setDeleteDependencies(dependenciesResponse.data);
          setDeleteDialogOpen(true);
        } catch (error) {
          console.error('Error checking dependencies:', error);
          alert('Error checking dependencies. Please try again.');
        } finally {
          setDeleteLoading(false);
        }
      } else {
        const itemType = 'aircraft category';
        if (!window.confirm(`Are you sure you want to archive this ${itemType}?`)) return;
        try {
          await axios.delete(`${effectiveType.endpoint}/${item._id}`);
          fetchData();
        } catch (error) {
          alert(error.response?.data?.message || `Error archiving ${itemType}`);
        }
      }
    } else if (currentType.key === 'manufacturers' && manufacturerSubTab === 2) {
      // Primary Models
      if (!item.isActive) {
        setItemToDelete(item);
        setDeleteLoading(true);
        try {
          const dependenciesResponse = await axios.get(`${effectiveType.endpoint}/${item._id}/dependencies`);
          setDeleteDependencies(dependenciesResponse.data);
          setDeleteDialogOpen(true);
        } catch (error) {
          console.error('Error checking dependencies:', error);
          alert('Error checking dependencies. Please try again.');
        } finally {
          setDeleteLoading(false);
        }
      } else {
        const itemType = 'primary model';
        if (!window.confirm(`Are you sure you want to archive this ${itemType}?`)) return;
        try {
          await axios.delete(`${effectiveType.endpoint}/${item._id}`);
          fetchData();
        } catch (error) {
          alert(error.response?.data?.message || `Error archiving ${itemType}`);
        }
      }
    } else if (currentType.key === 'assembly-types') {
      if (!item.isActive) {
        // Archived assembly type - check dependencies and show delete dialog
        setItemToDelete(item);
        setDeleteLoading(true);
        try {
          const dependenciesResponse = await axios.get(`${effectiveType.endpoint}/${item._id}/dependencies`);
          setDeleteDependencies(dependenciesResponse.data);
          setDeleteDialogOpen(true);
        } catch (error) {
          console.error('Error checking dependencies:', error);
          alert('Error checking dependencies. Please try again.');
        } finally {
          setDeleteLoading(false);
        }
      } else {
        // Active assembly type - archive it
        if (!window.confirm('Are you sure you want to archive this assembly type?')) return;
        try {
          await axios.delete(`${effectiveType.endpoint}/${item._id}`);
          fetchData();
        } catch (error) {
          alert(error.response?.data?.message || 'Error archiving assembly type');
        }
      }
    } else if (currentType.key === 'assembly-type-configurations') {
      if (!item.isActive) {
        // Archived assembly type configuration - check dependencies and show delete dialog
        setItemToDelete(item);
        setDeleteLoading(true);
        try {
          const dependenciesResponse = await axios.get(`${effectiveType.endpoint}/${item._id}/dependencies`);
          setDeleteDependencies(dependenciesResponse.data);
          setDeleteDialogOpen(true);
        } catch (error) {
          console.error('Error checking dependencies:', error);
          alert('Error checking dependencies. Please try again.');
        } finally {
          setDeleteLoading(false);
        }
      } else {
        // Active assembly type configuration - archive it
        if (!window.confirm('Are you sure you want to archive this assembly type configuration?')) return;
        try {
          await axios.delete(`${effectiveType.endpoint}/${item._id}`);
          fetchData();
        } catch (error) {
          alert(error.response?.data?.message || 'Error archiving assembly type configuration');
        }
      }
    } else {
      if (!window.confirm('Are you sure you want to archive this item?')) return;
      try {
        await axios.delete(`${effectiveType.endpoint}/${item._id}`);
        fetchData();
      } catch (error) {
        alert(error.response?.data?.message || 'Error archiving item');
      }
    }
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;

    setDeleteLoading(true);
    try {
      await axios.delete(`${effectiveType.endpoint}/${itemToDelete._id}?permanent=true`);
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      setDeleteDependencies(null);
      fetchData();
    } catch (error) {
      if (error.response?.data?.hasDependencies) {
        const itemType = (currentType.key === 'manufacturers' && manufacturerSubTab === 3)
          ? 'model variant' 
          : currentType.key === 'assembly-types' 
          ? 'assembly type' 
          : currentType.key === 'assembly-type-configurations'
          ? 'assembly type configuration'
          : effectiveType.label.slice(0, -1).toLowerCase();
        alert(`Cannot delete: This ${itemType} has dependencies that must be removed first.`);
      } else {
        const itemType = (currentType.key === 'manufacturers' && manufacturerSubTab === 3)
          ? 'model variant' 
          : currentType.key === 'assembly-types' 
          ? 'assembly type' 
          : currentType.key === 'assembly-type-configurations'
          ? 'assembly type configuration'
          : effectiveType.label.slice(0, -1).toLowerCase();
        alert(error.response?.data?.message || `Error deleting ${itemType}`);
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
    setDeleteDependencies(null);
  };

  const handleSubmit = async () => {
    const submitData = { ...formData };
    
    try {
      if (currentType.key === 'manufacturers' && manufacturerSubTab === 3) {
        // Model Variants
        if (!submitData.name || submitData.name.trim() === '') {
          alert('Model variant name is required.');
          return;
        }
        if (!submitData.manufacturer || submitData.manufacturer === '') {
          alert('Manufacturer is required.');
          return;
        }
        // Note: Type validation for Model Variants is handled later - they don't need type
        // Type is only required for Primary Models (handled in Manufacturer Setup tab)
      }
      
      if (currentType.key === 'manufacturers') {
        if (manufacturerSubTab === 0) {
          if (!submitData.name || submitData.name.trim() === '') {
            alert('Manufacturer name is required.');
            return;
          }
        } else if (manufacturerSubTab === 1) {
          if (!submitData.category || submitData.category.trim() === '') {
            alert('Category is required.');
            return;
          }
        } else if (manufacturerSubTab === 2) {
          if (!submitData.name || submitData.name.trim() === '') {
            alert('Primary model name is required.');
            return;
          }
          if (!submitData.manufacturer || submitData.manufacturer === '') {
            alert('Manufacturer name is required.');
            return;
          }
          if (!submitData.category || submitData.category === '') {
            alert('Category is required.');
            return;
          }
          if (!submitData.type || submitData.type === '') {
            alert('Aircraft type is required.');
            return;
          }
        }
      }
      
      if (currentType.key === 'assembly-types') {
        if (!submitData.name || submitData.name.trim() === '') {
          alert('Assembly Type name is required.');
          return;
        }
        const assemblyTypeName = submitData.name.trim().toLowerCase();
        if (assemblyTypeName !== 'airframe' && (!submitData.ataChapterId || submitData.ataChapterId === '')) {
          alert('ATA Chapter is required for all Assembly Types except "Airframe".');
          return;
        }
      }
      
      if (currentType.key === 'agencies') {
        if (!submitData.name || submitData.name.trim() === '') {
          alert('Agency name is required.');
          return;
        }
        if (!submitData.code || submitData.code.trim() === '') {
          alert('Agency code is required.');
          return;
        }
        if (!Array.isArray(submitData.types) || submitData.types.length === 0) {
          alert('At least one agency type is required.');
          return;
        }
        if (submitData.contactInfo && typeof submitData.contactInfo === 'object') {
          submitData.contactInfo = {
            email: (submitData.contactInfo.email || '').trim() || undefined,
            phone: (submitData.contactInfo.phone || '').trim() || undefined,
            address: (submitData.contactInfo.address || '').trim() || undefined
          };
        }
        if (typeof submitData.certifications === 'string') {
          submitData.certifications = submitData.certifications.split(',').map((c) => c.trim()).filter(Boolean);
        }
        if (!Array.isArray(submitData.certifications)) {
          submitData.certifications = submitData.certifications ? [submitData.certifications] : [];
        }
      }
      if (currentType.key === 'operators') {
        if (!submitData.name || submitData.name.trim() === '') {
          alert('Operator name is required.');
          return;
        }
        if (!submitData.code || submitData.code.trim() === '') {
          alert('Operator code is required.');
          return;
        }
        if (!submitData.address || submitData.address.trim() === '') {
          alert('Operator address is required.');
          return;
        }
        if (!submitData.gmail || submitData.gmail.trim() === '') {
          alert('Gmail is required.');
          return;
        }
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailPattern.test(submitData.gmail.trim())) {
          alert('Please enter a valid Gmail address.');
          return;
        }
        if (!submitData.number || submitData.number.trim() === '') {
          alert('Operator number is required.');
          return;
        }
        if (!Array.isArray(submitData.personToContact) || submitData.personToContact.length === 0) {
          alert('At least one contact person is required.');
          return;
        }
        submitData.personToContact = submitData.personToContact
          .map((person) => ({
            name: (person?.name || '').trim(),
            number: (person?.number || '').trim()
          }))
          .filter((person) => person.name || person.number);
        if (submitData.personToContact.length === 0) {
          alert('At least one contact person is required.');
          return;
        }
        const invalidContact = submitData.personToContact.find((person) => !person.name || !person.number);
        if (invalidContact) {
          alert('Each contact person must have both name and number.');
          return;
        }
        if (!submitData.template || submitData.template.trim() === '') {
          alert('Template header letterpad upload is required.');
          return;
        }
      }
      if (currentType.key === 'assembly-type-configurations') {
        // Validate ATA Chapter is selected (for new configurations)
        if (!selectedItem && (!submitData.selectedAtaChapterId || submitData.selectedAtaChapterId === '')) {
          alert('ATA Chapter is required. Please select an ATA Chapter first.');
          return;
        }
        // Validate Assembly Type is selected
        if (!submitData.assemblyType || submitData.assemblyType === '') {
          alert('Assembly Type is required. Please select an Assembly Type.');
          return;
        }
        // Remove selectedAtaChapterId from submit data (it's only for UI filtering)
        delete submitData.selectedAtaChapterId;
        
        if (submitData.columns) {
          if (typeof submitData.columns === 'string') {
            try {
              submitData.columns = JSON.parse(submitData.columns);
            } catch (e) {
              alert('Invalid JSON format for columns. Please check the format.');
              return;
            }
          }
          if (!Array.isArray(submitData.columns) || submitData.columns.length === 0) {
            alert('At least one column is required.');
            return;
          }
          for (let i = 0; i < submitData.columns.length; i++) {
            const col = submitData.columns[i];
            if (!col.name || !col.label || !col.type || col.order === undefined) {
              alert(`Column ${i + 1} is missing required fields (name, label, type, or order).`);
              return;
            }
          }
        }
        if (submitData.assemblyType && typeof submitData.assemblyType === 'object') {
          submitData.assemblyType = submitData.assemblyType._id || submitData.assemblyType;
        }
      }
      
      if (currentType.key === 'assembly-types') {
        if (submitData.ataChapterId) {
          if (typeof submitData.ataChapterId === 'object') {
            submitData.ataChapterId = submitData.ataChapterId._id || submitData.ataChapterId;
          }
        }
        if (submitData.name && submitData.name.trim().toLowerCase() === 'airframe') {
          delete submitData.ataChapterId;
        }
      }
      
      if (currentType.key === 'part-categories') {
        if (submitData.primaryCategory) {
          if (typeof submitData.primaryCategory === 'object') {
            submitData.primaryCategory = submitData.primaryCategory._id || submitData.primaryCategory;
          }
          // Validate it's a valid ObjectId format
          const objectIdPattern = /^[0-9a-fA-F]{24}$/;
          if (!objectIdPattern.test(submitData.primaryCategory)) {
            // If it's not a valid ObjectId and not empty, delete it
            if (submitData.primaryCategory.trim() !== '') {
              delete submitData.primaryCategory;
            } else {
              submitData.primaryCategory = undefined;
            }
          }
        }
      }
      
      if (currentType.key === 'manufacturers' && (manufacturerSubTab === 2 || manufacturerSubTab === 3)) {
        if (submitData.manufacturer) {
          if (typeof submitData.manufacturer === 'object') {
            submitData.manufacturer = submitData.manufacturer._id || submitData.manufacturer;
          }
        }
        if (manufacturerSubTab === 2 && submitData.category) {
          if (typeof submitData.category === 'object') {
            submitData.category = submitData.category._id || submitData.category;
          }
        }
        if (submitData.primaryModel) {
          if (typeof submitData.primaryModel === 'object') {
            submitData.primaryModel = submitData.primaryModel._id || submitData.primaryModel;
          }
          const objectIdPattern = /^[0-9a-fA-F]{24}$/;
          if (!objectIdPattern.test(submitData.primaryModel)) {
            // For Model Variants, primaryModel is required, so don't delete it if invalid
            if (manufacturerSubTab === 3) {
              alert('Invalid Primary Model selected. Please select a valid Primary Model.');
              return;
            }
            delete submitData.primaryModel;
          }
        }
      }
      
      if (currentType.key === 'manufacturers') {
        if (manufacturerSubTab === 2) {
          delete submitData.forAssembly;
          delete submitData.primaryModel;
        }
      }
      
      Object.keys(submitData).forEach(key => {
        const value = submitData[key];
        if (value === '' || value === null || value === undefined) {
          delete submitData[key];
        } else if (typeof value === 'string') {
          const trimmed = value.trim();
          if (trimmed === '') {
            delete submitData[key];
          } else {
            submitData[key] = trimmed;
          }
        }
      });
      
      // Validate Primary Model is required for Model Variants
      if (currentType.key === 'manufacturers' && manufacturerSubTab === 3) {
        if (!submitData.primaryModel) {
          alert('Primary Model is required for Model Variants. Please select a Primary Model.');
          return;
        }
        if (!submitData.manufacturer) {
          alert('Manufacturer is required.');
          return;
        }
        // Remove type field for Model Variants (not needed - it comes from Primary Model)
        delete submitData.type;
      }
      
      // Validate Type is required for Primary Models (in Manufacturer Setup)
      if (currentType.key === 'manufacturers' && manufacturerSubTab === 2) {
        if (!submitData.type) {
          alert('Type is required. Please select either "Fixed Wing" or "Rotary Wing".');
          return;
        }
      }
      
      if (currentType.key === 'manufacturers' && manufacturerSubTab === 3) {
        console.log('=== MODEL VARIANT SUBMISSION DEBUG ===');
        console.log('Full submitData:', JSON.stringify(submitData, null, 2));
        console.log('Manufacturer:', submitData.manufacturer, 'Primary Model:', submitData.primaryModel, 'Name:', submitData.name);
      }
      
      if (selectedItem) {
        await axios.put(`${effectiveType.endpoint}/${selectedItem._id}`, submitData);
      } else {
        await axios.post(effectiveType.endpoint, submitData);
      }
      setOpen(false);
      fetchData();
      if (currentType.key === 'assembly-type-configurations') {
        fetchAssemblyTypeConfigurations();
      }
      if (currentType.key === 'manufacturers' && manufacturerSubTab === 3) {
        fetchModels();
      }
      if (currentType.key === 'manufacturers') {
        fetchManufacturerNames();
        fetchManufacturerCategories();
        if (manufacturerSubTab === 2) {
          fetchModels(true);
        }
      }
      if (currentType.key === 'part-categories') {
        fetchPartCategories();
      }
      
      // Dispatch custom event to notify other components of master data updates
      // This will trigger dropdown refreshes in forms like Assembly, Component, Part, etc.
      const updatedTypes = [];
      if (currentType.key === 'assembly-types') {
        updatedTypes.push('assemblyTypes', 'ataChapters');
      } else if (currentType.key === 'ata-chapters') {
        updatedTypes.push('ataChapters');
      } else if (currentType.key === 'manufacturers') {
        if (manufacturerSubTab === 0) {
          // Manufacturer Names
          updatedTypes.push('manufacturerNames');
        } else if (manufacturerSubTab === 2) {
          // Primary Models
          updatedTypes.push('models', 'manufacturerNames');
        } else if (manufacturerSubTab === 3) {
          // Model Variants
          updatedTypes.push('models', 'manufacturerNames');
        }
      } else if (currentType.key === 'licenses') {
        updatedTypes.push('licenses');
      } else if (currentType.key === 'agencies') {
        updatedTypes.push('agencies');
      } else if (currentType.key === 'operators') {
        updatedTypes.push('operators');
      }
      
      if (updatedTypes.length > 0) {
        window.dispatchEvent(new CustomEvent('masterDataUpdated', {
          detail: { updatedTypes }
        }));
      }
      
      setSelectedItem(null);
    } catch (error) {
      console.error('Error saving item:', error);
      console.error('Error response:', error.response?.data);
      
      let errorMessage = 'Error saving item';
      
      if (error.response?.data) {
        if (error.response.data.errors && Array.isArray(error.response.data.errors)) {
          const errorMessages = error.response.data.errors.map(err => {
            const field = err.param || err.field || 'Field';
            const msg = err.msg || err.message || 'Invalid value';
            return `${field}: ${msg}`;
          });
          errorMessage = errorMessages.join('\n\n');
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        }
      }
      
      let userFriendlyMessage = errorMessage;
      
      if (currentType.key === 'manufacturers' && manufacturerSubTab === 3 && errorMessage.includes('already exists')) {
        const modelName = submitData?.name || formData?.name || 'this name';
        userFriendlyMessage = `A model variant with the name "${modelName}" already exists for this manufacturer.\n\nThis model variant might be archived (not visible in the list). Please:\n- Use a different name, or\n- Select a different manufacturer, or\n- Check if you need to edit the existing model variant instead.\n\nNote: Archived model variants are hidden from the list but still exist in the database.`;
      }
      
      alert(`Error: ${userFriendlyMessage}\n\nCheck the browser console (F12) for more details.`);
    }
  };

  const handleAddColumn = () => {
    const currentColumns = Array.isArray(formData.columns) ? formData.columns : [];
    const newOrder = currentColumns.length > 0 
      ? Math.max(...currentColumns.map(c => c.order || 0)) + 1 
      : 1;
    const newColumn = {
      name: '',
      label: '',
      type: 'text',
      unit: '',
      required: false,
      order: newOrder
    };
    setFormData({ ...formData, columns: [...currentColumns, newColumn] });
  };

  const handleRemoveColumn = (index) => {
    const currentColumns = Array.isArray(formData.columns) ? formData.columns : [];
    const newColumns = currentColumns.filter((_, i) => i !== index);
    newColumns.forEach((col, i) => {
      col.order = i + 1;
    });
    setFormData({ ...formData, columns: newColumns });
  };

  const handleColumnChange = (index, field, value) => {
    const currentColumns = Array.isArray(formData.columns) ? [...formData.columns] : [];
    currentColumns[index] = { ...currentColumns[index], [field]: value };
    setFormData({ ...formData, columns: currentColumns });
  };

  const handleShowConfigurationDetails = (configuration) => {
    setSelectedConfiguration(configuration);
    setConfigDetailsDialogOpen(true);
  };

  const handleCloseConfigurationDetails = () => {
    setConfigDetailsDialogOpen(false);
    setSelectedConfiguration(null);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if clicking the same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Sort data based on sortField and sortDirection
  const sortedData = useMemo(() => {
    if (!sortField || !data.length) return data;
    
    const sorted = [...data].sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
      
      // Handle nested objects (e.g., ataChapterId, manufacturer, etc.)
      if (typeof aValue === 'object' && aValue !== null) {
        aValue = aValue.name || aValue.code || aValue.category || String(aValue._id) || '';
      }
      if (typeof bValue === 'object' && bValue !== null) {
        bValue = bValue.name || bValue.code || bValue.category || String(bValue._id) || '';
      }
      
      // Convert to strings for comparison
      aValue = aValue !== undefined && aValue !== null ? String(aValue).toLowerCase() : '';
      bValue = bValue !== undefined && bValue !== null ? String(bValue).toLowerCase() : '';
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    
    return sorted;
  }, [data, sortField, sortDirection]);

  // Filter data based on search query across all columns
  const filteredData = useMemo(() => {
    if (!searchQuery.trim() || !sortedData.length) return sortedData;
    
    const query = searchQuery.toLowerCase().trim();
    
    return sortedData.filter((item) => {
      // Search across all fields in effectiveType.fields
      return effectiveType.fields.some((field) => {
        const cellValue = formatCellValue(item, field, currentType, manufacturerSubTab);
        return String(cellValue).toLowerCase().includes(query);
      });
    });
  }, [sortedData, searchQuery, effectiveType, currentType, manufacturerSubTab]);

  const canEdit = [ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER, ROLES.CA_MANAGER, ROLES.MRO_MANAGER, ROLES.STORE_INSPECTOR, ROLES.STORE_EXECUTIVE].includes(user?.role);
  const canDelete = [ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER, ROLES.CA_MANAGER, ROLES.MRO_MANAGER].includes(user?.role);

  return {
    // State
    manufacturerSubTab,
    setManufacturerSubTab,
    data,
    open,
    setOpen,
    selectedItem,
    formData,
    setFormData,
    loading,
    assemblyTypes,
    manufacturers,
    models,
    ataChapters,
    manufacturerNames,
    manufacturerCategories,
    assemblyTypeConfigurations,
    partCategories,
    showArchived,
    setShowArchived,
    deleteDialogOpen,
    itemToDelete,
    deleteDependencies,
    deleteLoading,
    configDetailsDialogOpen,
    selectedConfiguration,
    canEdit,
    canDelete,
    // Computed
    currentType,
    effectiveType,
    masterDataTypes,
    // Handlers
    handleAdd,
    handleEdit,
    handleRestore,
    handleDelete,
    handleConfirmDelete,
    handleCloseDeleteDialog,
    handleSubmit,
    handleAddColumn,
    handleRemoveColumn,
    handleColumnChange,
    handleShowConfigurationDetails,
    handleCloseConfigurationDetails,
    // Sorting
    sortField,
    sortDirection,
    handleSort,
    sortedData,
    // Search
    searchQuery,
    setSearchQuery,
    filteredData,
    // Data fetching (exposed for refresh after operations)
    fetchData,
    fetchModels
  };
};
